import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  RefreshCw, 
  CheckCircle, 
  AlertTriangle, 
  Wifi, 
  WifiOff,
  Download,
  Clock,
  Users,
  FileText,
  DollarSign,
  Zap
} from 'lucide-react';
import toast from 'react-hot-toast';
import { sincronizacaoService } from '../services/api';

const Sincronizacao = () => {
  const [isSincronizing, setIsSincronizing] = useState(false);
  const queryClient = useQueryClient();

  const { data: statusData, isLoading: statusLoading, error: statusError } = useQuery({
    queryKey: ['api-status'],
    queryFn: sincronizacaoService.statusApi,
    refetchInterval: 30000, // Verifica a cada 30 segundos
  });

  const syncMutation = useMutation({
    mutationFn: () => sincronizacaoService.sincronizar(),
    onMutate: () => {
      setIsSincronizing(true);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['clientes'] });
      queryClient.invalidateQueries({ queryKey: ['contas-receber'] });
      toast.success('Sincronização realizada com sucesso!');
      
      if (data.contas_sincronizadas > 0) {
        toast.success(`${data.contas_sincronizadas} contas sincronizadas`);
      }
      if (data.clientes_criados > 0) {
        toast.success(`${data.clientes_criados} clientes criados`);
      }
    },
    onError: (error) => {
      toast.error('Erro na sincronização: ' + error.message);
    },
    onSettled: () => {
      setIsSincronizing(false);
    },
  });

  const syncTodasMutation = useMutation({
    mutationFn: () => sincronizacaoService.sincronizarTodas(),
    onMutate: () => {
      setIsSincronizing(true);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['clientes'] });
      queryClient.invalidateQueries({ queryKey: ['contas-receber'] });
      toast.success('Sincronização completa realizada com sucesso!');
      
      if (data.contas_sincronizadas > 0) {
        toast.success(`${data.contas_sincronizadas} contas sincronizadas`);
      }
      if (data.clientes_criados > 0) {
        toast.success(`${data.clientes_criados} clientes criados`);
      }
    },
    onError: (error) => {
      toast.error('Erro na sincronização completa: ' + error.message);
    },
    onSettled: () => {
      setIsSincronizing(false);
    },
  });

  const syncUltraRapidoMutation = useMutation({
    mutationFn: () => sincronizacaoService.sincronizarUltraRapido(),
    onMutate: () => {
      setIsSincronizing(true);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['clientes'] });
      queryClient.invalidateQueries({ queryKey: ['contas-receber'] });
      toast.success('Sincronização ULTRA RÁPIDA realizada com sucesso!');
      
      if (data.contas_sincronizadas > 0) {
        toast.success(`${data.contas_sincronizadas} contas sincronizadas`);
      }
      if (data.clientes_criados > 0) {
        toast.success(`${data.clientes_criados} clientes criados`);
      }
      if (data.tempo_estimado) {
        toast.success(`Tempo estimado: ${data.tempo_estimado}`);
      }
    },
    onError: (error) => {
      toast.error('Erro na sincronização ultra rápida: ' + error.message);
    },
    onSettled: () => {
      setIsSincronizing(false);
    },
  });

  const syncCompletaMutation = useMutation({
    mutationFn: () => sincronizacaoService.sincronizarCompleta(),
    onMutate: () => {
      setIsSincronizing(true);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['clientes'] });
      queryClient.invalidateQueries({ queryKey: ['contas-receber'] });
      toast.success('Sincronização COMPLETA realizada com sucesso!');
      
      if (data.contas_sincronizadas > 0) {
        toast.success(`${data.contas_sincronizadas} contas sincronizadas`);
      }
      if (data.clientes_criados > 0) {
        toast.success(`${data.clientes_criados} clientes criados`);
      }
      if (data.tempo_total) {
        toast.success(`Tempo total: ${data.tempo_total}`);
      }
    },
    onError: (error) => {
      toast.error('Erro na sincronização completa: ' + error.message);
    },
    onSettled: () => {
      setIsSincronizing(false);
    },
  });

  const syncInteligenteMutation = useMutation({
    mutationFn: () => sincronizacaoService.sincronizarInteligente(),
    onMutate: () => {
      setIsSincronizing(true);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['clientes'] });
      queryClient.invalidateQueries({ queryKey: ['contas-receber'] });
      toast.success('Sincronização INTELIGENTE realizada com sucesso!');
      
      if (data.processadas > 0) {
        toast.success(`${data.processadas} contas processadas`);
      }
      if (data.atualizadas > 0) {
        toast.success(`${data.atualizadas} contas atualizadas`);
      }
      if (data.novas > 0) {
        toast.success(`${data.novas} contas novas`);
      }
    },
    onError: (error) => {
      toast.error('Erro na sincronização inteligente: ' + error.message);
    },
    onSettled: () => {
      setIsSincronizing(false);
    },
  });

  const handleSincronizar = () => {
    syncMutation.mutate();
  };

  const handleSincronizarTodas = () => {
    syncTodasMutation.mutate();
  };

  const handleSincronizarUltraRapido = () => {
    syncUltraRapidoMutation.mutate();
  };

  const handleSincronizarCompleta = () => {
    syncCompletaMutation.mutate();
  };

  const handleSincronizarInteligente = () => {
    syncInteligenteMutation.mutate();
  };

  // Verifica se a API está online
  const statusRaw = statusData?.status;
  const isApiOnline = statusRaw === 'conectado' || statusRaw === 'online' || statusRaw === 'success';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sincronização</h1>
          <p className="mt-2 text-sm text-gray-700">
            Sincronize dados com a API externa
          </p>
        </div>
      </div>

      {/* Status da API */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-gray-900">Status da API Externa</h3>
              <p className="text-sm text-gray-500">Verificação automática a cada 30 segundos</p>
            </div>
            {statusLoading ? (
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            ) : isApiOnline ? (
              <div className="flex items-center text-green-600">
                <Wifi className="h-6 w-6 mr-2" />
                <span className="font-medium">Online</span>
              </div>
            ) : (
              <div className="flex items-center text-red-600">
                <WifiOff className="h-6 w-6 mr-2" />
                <span className="font-medium">Offline</span>
              </div>
            )}
          </div>

          {statusData && (
            <div className="mt-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Status:</span>
                <span className={`font-medium ${isApiOnline ? 'text-green-600' : 'text-red-600'}`}>
                  {statusData?.status}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Mensagem:</span>
                <span className="text-gray-900">{statusData?.message}</span>
              </div>
              {statusData?.contas_disponiveis !== undefined && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Contas disponíveis:</span>
                  <span className="text-gray-900">{statusData.contas_disponiveis}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Última Sincronização */}
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-gray-900">Última Sincronização</h3>
              <p className="text-sm text-gray-500">Dados mais recentes do sistema</p>
            </div>
            <Clock className="h-6 w-6 text-gray-400" />
          </div>

          <div className="mt-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Status:</span>
              <span className="text-sm font-medium text-gray-900">
                {isSincronizing ? 'Sincronizando...' : 'Pronto'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">API:</span>
              <span className={`text-sm font-medium ${isApiOnline ? 'text-green-600' : 'text-red-600'}`}>
                {isApiOnline ? 'Disponível' : 'Indisponível'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Botão de Sincronização */}
      <div className="card">
        <div className="text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Sincronizar Dados</h3>
          <p className="text-sm text-gray-500 mb-6">
            Escolha o tipo de sincronização desejado. A sincronização ultra rápida é recomendada.
          </p>
          
          <div className="mb-6 space-y-3 text-sm text-gray-600">
            <div className="flex items-start">
              <div className="w-2 h-2 bg-red-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
              <div>
                <strong>COMPLETA (91.000+ contas):</strong> Sincroniza TODAS as contas da API com paginação (10-15 min)
              </div>
            </div>
            <div className="flex items-start">
              <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
              <div>
                <strong>ULTRA RÁPIDO:</strong> Sincroniza 250 contas em 30 segundos
              </div>
            </div>
            <div className="flex items-start">
              <div className="w-2 h-2 bg-primary-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
              <div>
                <strong>Contas em Aberto:</strong> Sincroniza apenas contas não liquidadas
              </div>
            </div>
            <div className="flex items-start">
              <div className="w-2 h-2 bg-green-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
              <div>
                <strong>Todas as Contas:</strong> Sincroniza todas as contas (método anterior)
              </div>
            </div>
            <div className="flex items-start">
              <div className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
              <div>
                <strong>INTELIGENTE:</strong> Identifica data mais antiga e sincroniza apenas o necessário (MAIS RÁPIDO!)
              </div>
            </div>
          </div>
          
          <div className="space-y-4">
            {/* Botão COMPLETO */}
            <button
              onClick={handleSincronizarCompleta}
              disabled={!isApiOnline || isSincronizing}
              className={`inline-flex items-center px-6 py-3 rounded-lg font-medium transition-colors duration-200 ${
                !isApiOnline || isSincronizing
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-red-500 text-white hover:bg-red-600 focus:ring-2 focus:ring-red-500 focus:ring-offset-2'
              }`}
            >
              {isSincronizing ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                  Sincronizando...
                </>
              ) : (
                <>
                  <Download className="h-5 w-5 mr-3" />
                  🔥 COMPLETA (91.000+ contas)
                </>
              )}
            </button>

            <div className="text-sm text-gray-500">ou</div>

            {/* Botão ULTRA RÁPIDO */}
            <button
              onClick={handleSincronizarUltraRapido}
              disabled={!isApiOnline || isSincronizing}
              className={`inline-flex items-center px-6 py-3 rounded-lg font-medium transition-colors duration-200 ${
                !isApiOnline || isSincronizing
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-yellow-500 text-white hover:bg-yellow-600 focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2'
              }`}
            >
              {isSincronizing ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                  Sincronizando...
                </>
              ) : (
                <>
                  <Zap className="h-5 w-5 mr-3" />
                  ⚡ ULTRA RÁPIDO (30s)
                </>
              )}
            </button>

            <div className="text-sm text-gray-500">ou</div>

            {/* Botão Contas em Aberto */}
            <button
              onClick={handleSincronizar}
              disabled={!isApiOnline || isSincronizing}
              className={`inline-flex items-center px-6 py-3 rounded-lg font-medium transition-colors duration-200 ${
                !isApiOnline || isSincronizing
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-primary-600 text-white hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2'
              }`}
            >
              {isSincronizing ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                  Sincronizando...
                </>
              ) : (
                <>
                  <RefreshCw className="h-5 w-5 mr-3" />
                  Contas em Aberto
                </>
              )}
            </button>

            <div className="text-sm text-gray-500">ou</div>

            {/* Botão Todas as Contas */}
            <button
              onClick={handleSincronizarTodas}
              disabled={!isApiOnline || isSincronizing}
              className={`inline-flex items-center px-6 py-3 rounded-lg font-medium transition-colors duration-200 ${
                !isApiOnline || isSincronizing
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-green-600 text-white hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2'
              }`}
            >
              {isSincronizing ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                  Sincronizando...
                </>
              ) : (
                <>
                  <Download className="h-5 w-5 mr-3" />
                  Todas as Contas
                </>
              )}
            </button>

            <div className="text-sm text-gray-500">ou</div>

            {/* Botão INTELIGENTE */}
            <button
              onClick={handleSincronizarInteligente}
              disabled={!isApiOnline || isSincronizing}
              className={`inline-flex items-center px-6 py-3 rounded-lg font-medium transition-colors duration-200 ${
                !isApiOnline || isSincronizing
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-purple-600 text-white hover:bg-purple-700 focus:ring-2 focus:ring-purple-500 focus:ring-offset-2'
              }`}
            >
              {isSincronizing ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                  Sincronizando...
                </>
              ) : (
                <>
                  <Zap className="h-5 w-5 mr-3" />
                  🧠 INTELIGENTE (MAIS RÁPIDO!)
                </>
              )}
            </button>

            <div className="text-sm text-gray-500">ou</div>
          </div>

          {!isApiOnline && (
            <p className="mt-3 text-sm text-red-600">
              A API externa está indisponível. Verifique a conexão e tente novamente.
            </p>
          )}
        </div>
      </div>

      {/* Resultado da Sincronização */}
      {(syncMutation.data || syncTodasMutation.data || syncUltraRapidoMutation.data || syncCompletaMutation.data || syncInteligenteMutation.data) && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Resultado da Sincronização</h3>
            <CheckCircle className="h-6 w-6 text-green-500" />
          </div>
          
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
            <div className="flex items-center p-4 bg-green-50 rounded-lg">
              <Users className="h-8 w-8 text-green-600 mr-3" />
              <div>
                <p className="text-sm font-medium text-green-900">Clientes Criados</p>
                <p className="text-2xl font-bold text-green-600">
                  {(syncMutation.data || syncTodasMutation.data || syncUltraRapidoMutation.data || syncCompletaMutation.data || syncInteligenteMutation.data)?.clientes_criados || 0}
                </p>
              </div>
            </div>
            
            <div className="flex items-center p-4 bg-blue-50 rounded-lg">
              <FileText className="h-8 w-8 text-blue-600 mr-3" />
              <div>
                <p className="text-sm font-medium text-blue-900">Contas Sincronizadas</p>
                <p className="text-2xl font-bold text-blue-600">
                  {(syncMutation.data || syncTodasMutation.data || syncUltraRapidoMutation.data || syncCompletaMutation.data || syncInteligenteMutation.data)?.contas_sincronizadas || 0}
                </p>
              </div>
            </div>
            
            <div className="flex items-center p-4 bg-purple-50 rounded-lg">
              <DollarSign className="h-8 w-8 text-purple-600 mr-3" />
              <div>
                <p className="text-sm font-medium text-purple-900">Tempo Estimado</p>
                <p className="text-2xl font-bold text-purple-600">
                  {(syncUltraRapidoMutation.data?.tempo_estimado || syncCompletaMutation.data?.tempo_estimado || syncInteligenteMutation.data?.tempo_total) || 'N/A'}
                </p>
              </div>
            </div>

            <div className="flex items-center p-4 bg-orange-50 rounded-lg">
              <Download className="h-8 w-8 text-orange-600 mr-3" />
              <div>
                <p className="text-sm font-medium text-orange-900">Lotes Processados</p>
                <p className="text-2xl font-bold text-orange-600">
                  {syncCompletaMutation.data?.lotes_processados || 'N/A'}
                </p>
              </div>
            </div>
          </div>

          {(syncMutation.error || syncTodasMutation.error || syncUltraRapidoMutation.error || syncCompletaMutation.error || syncInteligenteMutation.error) && (
            <div className="mt-4 p-4 bg-red-50 rounded-lg">
              <div className="flex items-center">
                <AlertTriangle className="h-5 w-5 text-red-400 mr-2" />
                <p className="text-sm text-red-800">
                  <strong>Erro:</strong> {(syncMutation.error || syncTodasMutation.error || syncUltraRapidoMutation.error || syncCompletaMutation.error || syncInteligenteMutation.error)?.message}
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Informações Adicionais */}
      <div className="card">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Informações sobre a Sincronização</h3>
        
        <div className="space-y-4">
          <div>
            <h4 className="font-medium text-gray-900 mb-2">Tipos de sincronização:</h4>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• <strong>COMPLETA:</strong> 91.000+ contas, 10-15 minutos (RECOMENDADO)</li>
              <li>• <strong>ULTRA RÁPIDO:</strong> 250 contas, 30 segundos</li>
              <li>• <strong>Contas em Aberto:</strong> Apenas contas não liquidadas</li>
              <li>• <strong>Todas as Contas:</strong> Histórico completo (método anterior)</li>
              <li>• <strong>INTELIGENTE:</strong> Analisa mudanças e atualiza apenas o necessário</li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-medium text-gray-900 mb-2">O que é sincronizado:</h4>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Contas a receber</li>
              <li>• Informações dos clientes</li>
              <li>• Valores e datas de vencimento</li>
              <li>• Status de pagamento</li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-medium text-gray-900 mb-2">Frequência recomendada:</h4>
            <p className="text-sm text-gray-600">
              Use a sincronização ULTRA RÁPIDA diariamente para manter as informações atualizadas.
            </p>
          </div>
          
          <div>
            <h4 className="font-medium text-gray-900 mb-2">Observações:</h4>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• A sincronização é incremental (não duplica dados)</li>
              <li>• Dados existentes são atualizados automaticamente</li>
              <li>• O método ULTRA RÁPIDO é otimizado para performance</li>
              <li>• Certifique-se de que a API externa esteja disponível</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sincronizacao; 